import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return ''; }
};

export const DivergentContractsCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/contracts/divergentes`);
      setItems(res.data?.contracts || []);
    } catch (e) {
      // silent (403 for non-managers)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleResolve = async (c) => {
    setResolvingId(c.id);
    try {
      await axios.post(`${API_URL}/contracts/${c.id}/resolver-divergencia`);
      toast.success(`Divergência do contrato #${c.codigo} marcada como resolvida`);
      setItems(prev => prev.filter(i => i.id !== c.id));
    } catch (e) {
      toast.error('Erro ao resolver divergência');
    } finally {
      setResolvingId(null);
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <Card className="rounded-2xl border-red-200 bg-red-50/40" data-testid="divergent-contracts-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" />
          Contratos Divergentes por resolver
          <span className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-xs font-bold" data-testid="divergent-contracts-count">
            {items.length}
          </span>
        </CardTitle>
        <p className="text-xs text-slate-500">
          NIF registado em clientes com nomes diferentes. Verifique o comprovativo e marque como resolvido.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((c) => (
          <div
            key={c.id}
            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-xl bg-white border border-red-100"
            data-testid={`divergent-item-${c.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/contracts/${c.service_category}/${c.company}/${c.id}`}
                  className="font-semibold text-slate-800 hover:text-[#2D3E6F] hover:underline truncate"
                  data-testid={`divergent-link-${c.id}`}
                >
                  #{c.codigo} · {c.nome_cliente}
                </Link>
                <span className="text-xs font-mono text-slate-500">NIF {c.nif}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {c.company_name}
                {c.nif_conflito_com && (
                  <> · já registado como <strong className="text-red-600">{c.nif_conflito_com.nome_cliente}</strong> (#{c.nif_conflito_com.codigo})</>
                )}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Criado por {c.created_by_name || '—'} · {fmtDate(c.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to={`/contracts/${c.service_category}/${c.company}/${c.id}`}>
                <Button variant="outline" size="sm" className="h-8" data-testid={`divergent-view-${c.id}`}>
                  Ver <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
              <Button
                size="sm"
                className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleResolve(c)}
                disabled={resolvingId === c.id}
                data-testid={`divergent-resolve-${c.id}`}
              >
                {resolvingId === c.id
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <><CheckCircle2 className="w-3 h-3 mr-1" /> Resolver</>}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
